import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PracticeReport } from "@/components/PracticeReport";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reopens the ORIGINAL frozen Practice Report for an account-owned attempt.
// Reuses the universal PracticeReport renderer — no second report UI, no regeneration.
export default function PracticeHistoryReport() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { account, loading } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | notfound
  const [summary, setSummary] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (!loading && !account) { navigate("/signin", { replace: true }); return; }
    if (!account || fetched.current) return;
    fetched.current = true;
    (async () => {
      try {
        const r = await fetch(`${API}/account/practice-report/${reportId}`, { credentials: "include" });
        if (!r.ok) { setState("notfound"); return; }
        setSummary(await r.json());
        setState("ready");
      } catch {
        setState("notfound");
      }
    })();
  }, [loading, account, reportId, navigate]);

  if (state === "loading") {
    return (
      <div className="flex min-h-[100dvh] w-full items-center justify-center bg-[#0a0a0a]" data-testid="history-report-loading">
        <Loader2 size={22} className="animate-spin" style={{ color: ACCENT }} />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-4 bg-[#0a0a0a] px-6 text-center" data-testid="history-report-notfound">
        <p className="text-base text-white/70">This Practice Report isn't available.</p>
        <button
          onClick={() => navigate("/my-adaptience", { replace: true })}
          data-testid="history-report-back"
          className="rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/85 transition-colors hover:bg-white/5"
        >
          Back to My Adaptience
        </button>
      </div>
    );
  }

  return (
    <PracticeReport
      summary={summary}
      simTitle={summary.scenario}
      simId={summary.simulation_id}
      sessionId={summary.session_id}
      onBack={() => navigate("/my-adaptience")}
      onHome={() => navigate("/")}
      onRestart={() => navigate(summary.slug ? `/simulation/${summary.slug}` : "/simulations")}
    />
  );
}
